import { FC, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { PlaceholderTimeSeriesPanel } from '../ContentPlaceholder/PlaceholderTimeSeriesPanel';
import { Icon } from '../Icon/Icon';
import { LabelTooltip } from '../Label/LabelTooltip';
import { Spinner } from '../Spinner/Spinner';
import { Text } from '../Text/Text';
import { IProps as ITimeSeriesHighGraphProps, TimeSeriesHighGraph } from '../TimeSeriesHighGraph/TimeSeriesHighGraph';
import { Position, Tooltip } from '../Tooltip/Tooltip';
import { Alert } from '../Alert/legacy/Alert';
import { Box } from '../Box/Box';
import { Image } from '../Image/Image';
import { Paragraph } from '../Paragraph/Paragraph';
import { LinkButton } from '../LinkButton/LinkButton';
import keyPrimarySolid from './keys/key-primary-solid.svg';
import keyPrimaryDashed from './keys/key-primary-dashed.svg';
import keySecondarySolid from './keys/key-secondary-solid.svg';
import keySecondaryDashed from './keys/key-secondary-dashed.svg';

const PlaceholderWrapper = styled.div<{ panelHeight: number }>`
  display: grid;
  height: 100%;
  min-height: ${({ panelHeight }) => (panelHeight && panelHeight !== 0 ? panelHeight - 2 : 364)}px;
`;

export const DashboardPanel = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.white};
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
`;

export const ErrorElement = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  left: 50%;
  max-width: 410px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
`;

export const ErrorOverlay = styled.div`
  background: ${({ theme }) => theme.vars.newColor.white};
  height: 100%;
  opacity: 0.8;
  position: absolute;
  width: 100%;
`;

export const ErrorAction = styled.div`
  margin-left: 5px;
`;

export const ContentElement = styled.div<{ isError?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 45px 45px 35px;
  pointer-events: ${({ isError }) => (isError ? 'none' : 'auto')};
`;

export const HeadingElement = styled.div`
  align-items: center;
  display: flex;
`;

export const HeadingTooltipWrapper = styled.div`
  display: inline-block;
  margin-left: 13px;
`;

export const SpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 13px;
`;

export const IconElement = styled.span`
  margin-right: 13px;
`;

const FooterElement = styled.div<{ isError: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-left: 45px;
  pointer-events: ${({ isError }) => (isError ? 'none' : 'auto')};
`;

const GraphContainer = styled.span`
  flex: 1;
  min-height: 12.5rem;
`;

const getKeyImage = (variation: KeyVariation): string => {
  if (variation === 'primary-solid') {
    return keyPrimarySolid;
  } else if (variation === 'primary-dashed') {
    return keyPrimaryDashed;
  } else if (variation === 'secondary-solid') {
    return keySecondarySolid;
  } else if (variation === 'secondary-dashed') {
    return keySecondaryDashed;
  }

  return keyPrimarySolid;
};

export type KeyVariation = 'primary-solid' | 'primary-dashed' | 'secondary-solid' | 'secondary-dashed';
export interface IChartKey {
  enabled: boolean;
  text: string;
  variation?: KeyVariation;
}

export interface IProps extends ITimeSeriesHighGraphProps {
  /** Optional tooltip heading.  Defaults to undefined. */
  headingTooltipTitle?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  headingTooltipDescription?: string;
  /** Optional tooltip content.  Defaults to undefined. */
  headingTooltipContent?: JSX.Element;
  /** Optional tooltip width. Defaults to auto */
  headingTooltipWidth?: string;
  /** Optional tooltip position. Defaults to right. */
  headingTooltipPosition?: Position;
  /** Show content placeholder */
  showContentPlaceholder?: boolean;
  /** Show the spinner alongside the heading. */
  showHeadingSpinner?: boolean;
  /** Subtitle element shown below the heading. */
  subtitle?: JSX.Element;
  /** Keys to show below the subtitle & heading. */
  chartKeys?: IChartKey[];
  /** Icon to show alongside the heading. */
  icon?: string;
  /** Show error message. */
  isError?: boolean;
  /** Error message to display. (Default: 'Error loading graph data.')*/
  errorMessage?: string;
  /** Handle error action click. */
  handleErrorActionClick?: () => void;
  /** Error action message. (Default: 'Please refresh.')*/
  errorActionMessage?: string;
}

export const TimeSeriesHighPanel: FC<IProps> = ({
  chartKeys,
  children,
  errorMessage = 'Error loading graph data.',
  errorActionMessage = 'Please refresh.',
  handleErrorActionClick,
  heading,
  headingTooltipContent,
  headingTooltipDescription,
  headingTooltipPosition,
  headingTooltipTitle,
  headingTooltipWidth,
  icon,
  isError,
  showContentPlaceholder,
  showHeadingSpinner,
  subtitle,
  ...timeSeriesHighGraphProps
}) => {
  const theme = useTheme();

  const [chartHeight, setChartHeight] = useState(0);
  const [panelHeight, setPanelHeight] = useState(0);
  const graphContainer = useRef(null);

  useEffect(() => {
    if (graphContainer && graphContainer.current && chartHeight === 0) {
      setChartHeight(graphContainer.current.clientHeight);
    }
  }, [graphContainer, chartHeight]);

  const resizeChart = (width: number, height: number) => {
    if (height < panelHeight) {
      // If the panel height is shrinking reset the height of the graph to 0.
      setChartHeight(0);
    } else if (graphContainer.current && graphContainer.current.clientHeight) {
      setChartHeight(graphContainer.current.clientHeight);
    }

    setPanelHeight(height);
  };

  const renderHeading = () => {
    if (!heading) {
      return null;
    }

    return (
      <HeadingElement>
        {icon && (
          <IconElement>
            <Icon icon={icon} />
          </IconElement>
        )}

        <Text size={18} weight="bold">
          {heading}
        </Text>

        {(headingTooltipDescription || headingTooltipContent) && (
          <HeadingTooltipWrapper>
            <Tooltip
              position={headingTooltipPosition}
              html={
                <LabelTooltip
                  heading={headingTooltipTitle}
                  description={headingTooltipDescription}
                  content={headingTooltipContent}
                  width={headingTooltipWidth}
                />
              }
            >
              <Icon icon="question-circle" size="small" color={theme.vars.newColor.darkGS2} />
            </Tooltip>
          </HeadingTooltipWrapper>
        )}

        {showHeadingSpinner && (
          <SpinnerContainer>
            <Spinner size="small" />
          </SpinnerContainer>
        )}
      </HeadingElement>
    );
  };

  if (showContentPlaceholder) {
    return (
      <DashboardPanel>
        <ContentElement>
          {renderHeading()}

          <PlaceholderWrapper panelHeight={panelHeight}>
            <PlaceholderTimeSeriesPanel />
          </PlaceholderWrapper>
        </ContentElement>
      </DashboardPanel>
    );
  }

  return (
    <ReactResizeDetector handleHeight={true} onResize={resizeChart} refreshMode="throttle" refreshRate={150}>
      <DashboardPanel>
        <ContentElement isError={isError}>
          {renderHeading()}

          {subtitle}

          {chartKeys && (
            <Box verticalPositioning="center" padding="20px 0 10px" margin="0 30px 0 0">
              {chartKeys.map((chartKey, index) => {
                return chartKey.enabled ? (
                  <Box key={index} margin="0 10px 0 0" verticalPositioning="center">
                    <Box verticalPositioning="center" margin="0 10px 0 0">
                      <Image src={getKeyImage(chartKey.variation ? chartKey.variation : 'primary-solid')} height={17} />
                    </Box>

                    <Paragraph bold={true}>{chartKey.text}</Paragraph>
                  </Box>
                ) : null;
              })}
            </Box>
          )}

          <GraphContainer ref={graphContainer}>
            <TimeSeriesHighGraph chartHeight={chartHeight} heading={heading} {...timeSeriesHighGraphProps} />
          </GraphContainer>
        </ContentElement>

        <FooterElement isError={isError}>{children}</FooterElement>

        {isError && (
          <>
            <ErrorOverlay />

            <ErrorElement>
              <Alert
                variation="error"
                heading={errorMessage}
                headingElement={
                  handleErrorActionClick &&
                  errorActionMessage && (
                    <ErrorAction>
                      <LinkButton onClick={handleErrorActionClick}>{errorActionMessage}</LinkButton>
                    </ErrorAction>
                  )
                }
              />
            </ErrorElement>
          </>
        )}
      </DashboardPanel>
    </ReactResizeDetector>
  );
};

export default TimeSeriesHighPanel;
